import {
  CIRCA_BRAND_DEFAULTS,
  contrastingForegroundChannels,
  hexToHslChannels,
  normalizeBrandHexInput,
  relativeLuminance,
  softSurfaceChannels,
} from "@/lib/brandColors";

/** Shown when community.guidelines is null (not persisted until owner saves). */
export const DEFAULT_COMMUNITY_GUIDELINES = `Be respectful and constructive.
Stay on topic for this community.
No harassment, hate, or spam.
Do not share others' private information.
Moderators may remove content that breaks these guidelines.`;

/** Readable ink — light page (matches social-plane.css). */
const LIGHT_INK = "24 30% 12%";
const LIGHT_MUTED_INK = "24 12% 38%";

/** Readable ink — dark page. */
const DARK_INK = "36 30% 98%";
const DARK_MUTED_INK = "36 12% 72%";

/** Banner / CTA text contrast (aligned with contrastingForegroundChannels). */
const DARK_LUMINANCE_MAX = 0.45;

/**
 * Page light vs dark: only fairly dark seeds flip to the dark ladder.
 * Mid-bright brand blues (e.g. #78AEF0 ≈ 0.41) stay light.
 */
const PAGE_DARK_LUMINANCE_MAX = 0.3;

/** Light ladder: airy wash near Circa Social parchment. */
const LIGHT = {
  bg: { lightness: 93, maxSaturation: 40 },
  card: { lightness: 98, maxSaturation: 12 },
  muted: { lightness: 94, maxSaturation: 28 },
  border: { lightness: 86, maxSaturation: 24 },
  accentSurface: { lightness: 95, maxSaturation: 30 },
  chip: { lightness: 94, maxSaturation: 32 },
  orbitSoft: { lightness: 90, maxSaturation: 40 },
};

/** Dark ladder: near-black tinted canvas; cards slightly lifted. */
const DARK = {
  bg: { lightness: 9, maxSaturation: 40 },
  card: { lightness: 15, maxSaturation: 34 },
  muted: { lightness: 17, maxSaturation: 32 },
  border: { lightness: 23, maxSaturation: 28 },
  accentSurface: { lightness: 18, maxSaturation: 34 },
  chip: { lightness: 18, maxSaturation: 36 },
  orbitSoft: { lightness: 25, maxSaturation: 38 },
};

/**
 * Resolve banner colors: community brand → org brand → Circa defaults.
 * @param {object|null} community
 * @returns {{ brand_primary: string, brand_secondary: string, brand_accent: string }}
 */
export function resolveCommunityBrandColors(community) {
  const org = community?.org;
  const pick = (communityKey, orgKey, defaultKey) =>
    normalizeBrandHexInput(community?.[communityKey]) ||
    normalizeBrandHexInput(org?.[orgKey]) ||
    CIRCA_BRAND_DEFAULTS[defaultKey];

  return {
    brand_primary: pick("brand_primary", "brand_primary", "brand_primary"),
    brand_secondary: pick(
      "brand_secondary",
      "brand_secondary",
      "brand_secondary"
    ),
    brand_accent: pick("brand_accent", "brand_accent", "brand_accent"),
  };
}

/**
 * Auto light/dark from community-owned colors only (not org/Circa fallbacks).
 * Prefer brand_secondary, else brand_primary; neither set → light.
 * @param {object|null} communityOrForm
 * @returns {'light' | 'dark'}
 */
export function communityThemeMode(communityOrForm) {
  const secondary = normalizeBrandHexInput(communityOrForm?.brand_secondary);
  const primary = normalizeBrandHexInput(communityOrForm?.brand_primary);
  const seed = secondary || primary;
  if (!seed) return "light";
  return relativeLuminance(seed) <= PAGE_DARK_LUMINANCE_MAX ? "dark" : "light";
}

/** Alias for settings forms (same rules as communityThemeMode). */
export function communityThemeModeFromForm(form) {
  return communityThemeMode(form);
}

/**
 * @param {Record<string, string>} vars
 * @param {string} seedHex
 * @param {'light' | 'dark'} mode
 */
function applyCommunitySurfaces(vars, seedHex, mode) {
  const ladder = mode === "dark" ? DARK : LIGHT;
  const ink = mode === "dark" ? DARK_INK : LIGHT_INK;
  const mutedInk = mode === "dark" ? DARK_MUTED_INK : LIGHT_MUTED_INK;

  const bg = softSurfaceChannels(seedHex, ladder.bg);
  const card = softSurfaceChannels(seedHex, ladder.card);
  const muted = softSurfaceChannels(seedHex, ladder.muted);
  const border = softSurfaceChannels(seedHex, ladder.border);
  const accentSurface = softSurfaceChannels(seedHex, ladder.accentSurface);

  if (bg) vars["--background"] = bg;
  if (card) {
    vars["--card"] = card;
    vars["--popover"] = card;
  }
  if (muted) vars["--muted"] = muted;
  if (accentSurface) vars["--accent"] = accentSurface;
  if (border) {
    vars["--border"] = border;
    vars["--input"] = border;
  }
  vars["--foreground"] = ink;
  vars["--card-foreground"] = ink;
  vars["--popover-foreground"] = ink;
  vars["--muted-foreground"] = mutedInk;
}

/**
 * CSS vars for the community page. Secondary luminance picks light vs dark
 * surface ladder; primary drives CTAs. Scoped to the community page root.
 */
export function communityPageCssVars(community) {
  if (!community) return {};
  const colors = resolveCommunityBrandColors(community);
  const vars = {};
  const primary = colors.brand_primary;
  const secondary = colors.brand_secondary;
  const accent = colors.brand_accent;
  const mode = communityThemeMode(community);
  const ladder = mode === "dark" ? DARK : LIGHT;

  if (primary) {
    const p = hexToHslChannels(primary);
    const fg = contrastingForegroundChannels(primary);
    vars["--primary"] = p;
    vars["--primary-foreground"] = fg;
    vars["--ring"] = p;
  }

  if (accent) {
    const a = hexToHslChannels(accent);
    vars["--circa-orbit"] = a;
    vars["--circa-glow"] = a;
    const soft = softSurfaceChannels(accent, ladder.orbitSoft);
    if (soft) vars["--circa-orbit-soft"] = soft;
  }

  const surfaceSeed = secondary || primary;
  if (surfaceSeed) {
    applyCommunitySurfaces(vars, surfaceSeed, mode);
  }

  if (secondary) {
    const secSoft = softSurfaceChannels(secondary, ladder.chip);
    if (secSoft) {
      vars["--secondary"] = secSoft;
      vars["--secondary-foreground"] =
        mode === "dark" ? DARK_INK : LIGHT_INK;
    }
    const brandSec = hexToHslChannels(secondary);
    if (brandSec) vars["--brand-secondary"] = brandSec;
  }

  return vars;
}

/**
 * Solid primary → secondary header fill + accent top bar.
 * Banner text + Join CTA contrast from primary (independent of page mode).
 */
export function communityBannerStyle(colors) {
  const primary = colors.brand_primary;
  const secondary = colors.brand_secondary;
  const accent = colors.brand_accent;
  const fgChannels = contrastingForegroundChannels(primary);
  const fg = `hsl(${fgChannels})`;
  const primaryIsLight = relativeLuminance(primary) > DARK_LUMINANCE_MAX;
  const mutedFg = primaryIsLight
    ? "hsla(24, 30%, 8%, 0.72)"
    : "hsla(36, 30%, 98%, 0.85)";
  // Join CTA: high-contrast chip vs primary (independent of page light/dark).
  const ctaBg = fg;
  const ctaFg = primaryIsLight
    ? `hsl(${DARK_INK})`
    : `hsl(${LIGHT_INK})`;

  return {
    background: `linear-gradient(135deg, ${primary} 0%, ${primary} 55%, ${secondary} 100%)`,
    borderTop: `6px solid ${accent}`,
    color: fg,
    "--community-banner-fg": fg,
    "--community-banner-muted": mutedFg,
    "--community-banner-cta-bg": ctaBg,
    "--community-banner-cta-fg": ctaFg,
  };
}
